import {
  Badge,
  Box,
  HStack,
  Icon,
  Text,
  VStack
} from "@chakra-ui/react";
import React from "react";
import {
  FiCheck,
  FiCode,
  FiExternalLink,
  FiGithub,
  FiInfo,
  FiSettings,
  FiX
} from "react-icons/fi";

interface PathSummaryPanelProps {
  path: string;
  githubRepo: string;
  githubSubfolder: string;
  githubTag: string;
  metadata: string;
  forceExternalBrowser: boolean;
  pathAvailability: {
    checked: boolean;
    available: boolean;
    message?: string;
  };
}

export const PathSummaryPanel: React.FC<PathSummaryPanelProps> = ({
  path,
  githubRepo,
  githubSubfolder,
  githubTag,
  metadata,
  forceExternalBrowser,
  pathAvailability
}) => {
  return (
    <VStack gap={6} align="stretch">
      {/* Summary Panel */}
      <Box
        bg="white"
        shadow="sm"
        rounded="lg"
        p={6}
      >
        <HStack mb={4}>
          <Icon as={FiSettings} color="purple.500" boxSize={5} />
          <Text fontWeight="semibold" fontSize="lg">
            Configuration Summary
          </Text>
        </HStack>

        <VStack align="stretch" gap={3}>
            {/* Path Preview */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                Path URL
              </Text>
              {path ? (
                <Text fontSize="md" fontFamily="mono">
                  {path}
                </Text>
              ) : (
                <Text fontSize="md" color="gray.500">
                  Not configured
                </Text>
              )}
            </Box>

            {/* Repository Info */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>
                GitHub Repository
              </Text>
              {githubRepo ? (
                <Box p={2} bg="blue.50" rounded="md" borderLeft="3px solid" borderColor="blue.400">
                  <VStack align="start" gap={1}>
                    <HStack gap={2}>
                      <Icon as={FiGithub} color="gray.600" boxSize={4} />
                      <Text fontSize="sm" fontWeight="medium">
                        {githubRepo}
                      </Text>
                    </HStack>
                    
                    {githubSubfolder && (
                      <Text fontSize="xs" color="gray.500">
                        Subfolder: /{githubSubfolder}
                      </Text>
                    )}
                    
                    {githubTag && (
                      <HStack gap={2}>
                        <Text fontSize="xs" color="gray.500">Branch/Tag:</Text>
                        <Badge
                          colorScheme={
                            githubTag === "main" || githubTag === "master" 
                              ? "green" 
                              : githubTag.startsWith("v") 
                              ? "blue" 
                              : "gray"
                          }
                          fontSize="xs"
                        >
                          {githubTag}
                        </Badge>
                      </HStack>
                    )}
                  </VStack>
                </Box>
              ) : (
                <Text fontSize="sm" color="gray.500">
                  No repository selected
                </Text>
              )}
            </Box>

            {/* Advanced Settings */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={2}>
                Advanced Settings
              </Text>
              {(metadata || forceExternalBrowser) ? (
                <VStack align="start" gap={2}>
                  {metadata && (
                    <Box p={2} bg="gray.50" rounded="md">
                      <HStack gap={2}>
                        <Icon as={FiCode} color="gray.500" boxSize={3} />
                        <Text fontSize="xs" color="gray.600">
                          Page metadata configured
                        </Text>
                      </HStack>
                    </Box>
                  )}
                  {forceExternalBrowser && (
                    <Box p={2} bg="purple.50" rounded="md">
                      <HStack gap={2}>
                        <Icon as={FiExternalLink} color="purple.500" boxSize={3} />
                        <Text fontSize="xs" color="purple.700">
                          Force external browser enabled
                        </Text>
                      </HStack>
                    </Box>
                  )}
                </VStack>
              ) : (
                <Text fontSize="sm" color="gray.500">
                  No advanced settings configured
                </Text>
              )}
            </Box>

            {/* Status */}
            <Box>
              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                Status
              </Text>
              <Badge colorScheme="gray" size="md">
                Draft
              </Badge>
            </Box>

            {/* Validation Status */}
            {pathAvailability.checked && (
              <Box
                p={3}
                bg={pathAvailability.available ? "green.50" : "red.50"}
                border="1px"
                borderColor={pathAvailability.available ? "green.200" : "red.200"}
                rounded="md"
              >
                <HStack gap={2}>
                  <Icon
                    as={pathAvailability.available ? FiCheck : FiX}
                    color={pathAvailability.available ? "green.600" : "red.600"}
                    boxSize={4}
                  />
                  <Text
                    fontSize="xs"
                    color={pathAvailability.available ? "green.700" : "red.700"}
                    fontWeight="medium"
                  >
                    {pathAvailability.available 
                      ? "Path is available" 
                      : pathAvailability.message || "Path is not available"}
                  </Text>
                </HStack>
              </Box>
            )}
        </VStack>
      </Box>

      {/* Help Panel */}
      <Box
        bg="white"
        shadow="sm"
        rounded="lg"
        p={6}
      >
        <HStack mb={4}>
          <Icon as={FiInfo} color="blue.500" boxSize={5} />
          <Text fontWeight="semibold" fontSize="lg">
            Quick Tips
          </Text>
        </HStack>
        <VStack align="start" gap={2}>
          <Text fontSize="sm" color="gray.600">
            • Use descriptive paths like <Text as="span" fontFamily="mono" color="blue.600">/docs/api</Text>
          </Text>
          <Text fontSize="sm" color="gray.600">
            • Select stable branches for production paths
          </Text>
          <Text fontSize="sm" color="gray.600">
            • Subfolders help organize content within repos
          </Text>
          <Text fontSize="sm" color="gray.600">
            • Paths are case-sensitive and must start with /
          </Text>
        </VStack>
      </Box>
    </VStack>
  );
};